import React, { useState } from 'react';
import { FiHeart, FiTrash2, FiCornerDownRight } from 'react-icons/fi';
import { Comment } from '../../types';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { showToast } from '../../utils/toast';
import { getProfileImageUrl, DEFAULT_AVATAR_URL } from '../../utils/imageUtils';
// Date formatting helper
const formatDistanceToNow = (date: Date) => {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  }
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  }
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  }
  return date.toLocaleDateString();
};

interface CommentItemProps {
  comment: Comment;
  signalAuthorId: string;
  onReply?: (parentCommentId: string) => void;
  level?: number;
}

const CommentItem: React.FC<CommentItemProps> = ({ comment, signalAuthorId, onReply, level = 0 }) => {
  const { user } = useAuthStore();
  const userId = user?._id?.toString() || user?.id?.toString() || '';

  const [isLiked, setIsLiked] = useState(() => {
    return comment.likes?.some((likeId) => String(likeId) === userId) || false;
  });
  const [likesCount, setLikesCount] = useState(comment.likesCount || 0);
  const [isDeleting, setIsDeleting] = useState(false);

  const likeMutation = useApi.useLikeComment({
    onSuccess: (data) => {
      setIsLiked(data.isLiked);
      setLikesCount(data.likesCount);
      // Success feedback is handled by optimistic update
    },
    onError: (error: any) => {
      // Revert optimistic update on error
      setIsLiked(!isLiked);
      setLikesCount((prev) => (isLiked ? prev + 1 : prev - 1));
      showToast('error', error.response?.data?.error || 'Failed to like comment');
    },
  });

  const deleteMutation = useApi.useDeleteComment({
    onSuccess: () => {
      showToast('success', 'Comment deleted successfully');
      setIsDeleting(true);
    },
    onError: (error: any) => {
      showToast('error', error.response?.data?.error || 'Failed to delete comment');
    },
  });

  const handleLike = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (likeMutation.isPending) return;

    // Optimistic update
    setIsLiked(!isLiked);
    setLikesCount((prev) => (isLiked ? prev - 1 : prev + 1));

    likeMutation.mutate(comment._id);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleteMutation.isPending || !window.confirm('Are you sure you want to delete this comment?')) return;
    deleteMutation.mutate(comment._id);
  };

  const canDelete =
    userId === comment.author._id.toString() ||
    userId === signalAuthorId ||
    user?.role === 'admin';

  if (isDeleting) {
    return (
      <div className="text-muted text-sm italic p-2">
        Comment deleted
      </div>
    );
  }

  const isTopLevel = level === 0;
  const marginLeft = level > 0 ? 'ml-2 sm:ml-8' : '';

  return (
    <div className={`${marginLeft} ${isTopLevel ? 'border-b border-border pb-3 mb-3' : 'mb-2'}`}>
      <div className="flex items-start gap-2 sm:gap-3">
        <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-200 dark:border-gray-700 flex-shrink-0">
          <img
            src={getProfileImageUrl((comment.author as any)?.profileImage)}
            alt={comment.author.name}
            crossOrigin="anonymous"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = DEFAULT_AVATAR_URL;
            }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mb-1">
            <span className="font-semibold text-foreground text-sm break-anywhere">
              {comment.author.name}
            </span>
            {comment.author.role === 'master' && (
              <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                Master
              </span>
            )}
            <span className="text-xs text-muted">
              {formatDistanceToNow(new Date(comment.createdAt))}
            </span>
          </div>

          <p className="text-foreground text-sm mb-2 whitespace-pre-wrap break-words">
            {comment.text}
          </p>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <button
              onClick={handleLike}
              disabled={likeMutation.isPending}
              className={`flex items-center gap-1 text-sm transition-colors ${isLiked
                ? 'text-red-500 hover:text-red-600'
                : 'text-muted hover:text-foreground'
                }`}
            >
              <FiHeart className={isLiked ? 'fill-current' : ''} />
              <span>{likesCount}</span>
            </button>

            {onReply && (
              <button
                onClick={() => onReply(comment._id)}
                className="flex items-center gap-1 text-sm text-muted hover:text-foreground transition-colors"
              >
                <FiCornerDownRight />
                <span>Reply</span>
              </button>
            )}

            {canDelete && (
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-1 text-sm text-red-500 hover:text-red-600 transition-colors"
              >
                <FiTrash2 />
                <span>Delete</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommentItem;

